import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Star, Check, X, AlertTriangle } from 'lucide-react';
import { prefijosAPI } from '../services/api';
import { Modal, Button, Input, useConfirm, Tooltip, Badge, Card } from './ui';

/**
 * Panel de administracion de prefijos de numeracion (COT, MPW, ADV, ...).
 * Cada prefijo tiene su propio contador (`ultimo_numero`), color visual y
 * puede activarse/desactivarse. Solo uno puede ser default (se usa cuando
 * el usuario no elige explicitamente al crear una cotizacion).
 *
 * Todo se edita via modales para evitar cambios accidentales.
 */

const COLORES_SUGERIDOS = [
    '#3B82F6', // blue
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#10B981', // emerald
    '#F59E0B', // amber
    '#EF4444', // red
    '#14B8A6', // teal
    '#6366F1'  // indigo
];

const emptyForm = { prefijo: '', nombre: '', descripcion: '', color: COLORES_SUGERIDOS[0], ultimo_numero: 0, es_default: false, orden: 0, tipo: 'normal' };

const NumeracionesPanel = () => {
    const confirm = useConfirm();
    const [prefijos, setPrefijos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formModal, setFormModal] = useState({ open: false, editing: null, data: emptyForm });
    const [contadorModal, setContadorModal] = useState({ open: false, prefijo: null, valor: 0 });
    const [saving, setSaving] = useState(false);

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await prefijosAPI.getAll();
            // Normalizar booleans: MySQL devuelve 0/1 y `0 && ...` en JSX renderiza "0".
            const normalizados = (res.data || []).map(p => ({
                ...p,
                activo: !!p.activo,
                es_default: !!p.es_default,
            }));
            setPrefijos(normalizados);
        } catch (err) {
            toast.error('No pudimos cargar los prefijos');
        } finally {
            setLoading(false);
        }
    };

    // ============ CREAR / EDITAR ============
    const abrirCrear = () => {
        setFormModal({
            open: true,
            editing: null,
            data: { ...emptyForm, orden: prefijos.length }
        });
    };
    const abrirEditar = (p) => {
        setFormModal({
            open: true,
            editing: p.id,
            data: {
                prefijo: p.prefijo,
                nombre: p.nombre,
                descripcion: p.descripcion || '',
                color: p.color || COLORES_SUGERIDOS[0],
                ultimo_numero: p.ultimo_numero,
                es_default: !!p.es_default,
                orden: p.orden,
                tipo: p.tipo || 'normal'
            }
        });
    };
    const cerrarForm = () => setFormModal({ open: false, editing: null, data: emptyForm });

    const handleGuardar = async () => {
        const { editing, data } = formModal;
        if (!data.nombre.trim()) return toast.error('El nombre es obligatorio');
        if (!editing && !data.prefijo.trim()) return toast.error('El prefijo es obligatorio');
        if (!editing && !/^[A-Za-z0-9]{1,20}$/.test(data.prefijo.trim())) {
            return toast.error('El prefijo solo puede tener letras y números (max 20)');
        }
        setSaving(true);
        try {
            if (editing) {
                await prefijosAPI.update(editing, {
                    nombre: data.nombre,
                    descripcion: data.descripcion,
                    color: data.color,
                    orden: data.orden,
                    tipo: data.tipo
                });
                toast.success('Prefijo actualizado');
            } else {
                await prefijosAPI.create(data);
                toast.success(`Prefijo "${data.prefijo.toUpperCase()}" creado`);
            }
            cerrarForm();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    // ============ SET DEFAULT ============
    const handleSetDefault = async (p) => {
        if (p.es_default) return;
        const ok = await confirm({
            title: `¿Marcar "${p.prefijo}" como default?`,
            message: (
                <p>
                    Las cotizaciones nuevas usarán <strong>{p.prefijo}</strong> como prefijo por defecto
                    cuando el usuario no elija otro. El default anterior se desmarca.
                </p>
            ),
            confirmText: 'Sí, marcar default'
        });
        if (!ok) return;
        try {
            await prefijosAPI.setDefault(p.id);
            toast.success(`${p.prefijo} es ahora el prefijo default`);
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error');
        }
    };

    // ============ ACTIVAR / DESACTIVAR ============
    const handleToggleActivo = async (p) => {
        if (p.activo && p.es_default) {
            toast.error('No podés desactivar el default. Marcá otro primero.');
            return;
        }
        const accion = p.activo ? 'desactivar' : 'activar';
        const ok = await confirm({
            title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} "${p.prefijo}"?`,
            message: p.activo
                ? <p>El prefijo dejará de aparecer en el selector al crear cotizaciones. Las existentes NO se afectan.</p>
                : <p>El prefijo volverá a estar disponible en el selector al crear cotizaciones.</p>,
            confirmText: `Sí, ${accion}`,
            danger: p.activo
        });
        if (!ok) return;
        try {
            await prefijosAPI.toggleActivo(p.id);
            toast.success(`Prefijo ${accion}do`);
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error');
        }
    };

    // ============ ELIMINAR ============
    const handleEliminar = async (p) => {
        if (p.cotizaciones_count > 0) {
            toast.error(`No podés eliminar: ${p.cotizaciones_count} cotización${p.cotizaciones_count === 1 ? '' : 'es'} lo usa${p.cotizaciones_count === 1 ? '' : 'n'}. Podés desactivarlo.`);
            return;
        }
        const ok = await confirm({
            title: `¿Eliminar "${p.prefijo}"?`,
            message: <p>Vas a eliminar el prefijo <strong>{p.prefijo}</strong> definitivamente. No hay cotizaciones asociadas.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await prefijosAPI.delete(p.id);
            toast.success('Prefijo eliminado');
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error');
        }
    };

    // ============ EDITAR CONTADOR ============
    const abrirContador = (p) => {
        setContadorModal({ open: true, prefijo: p, valor: p.ultimo_numero });
    };
    const cerrarContador = () => setContadorModal({ open: false, prefijo: null, valor: 0 });
    const handleGuardarContador = async () => {
        const nuevo = parseInt(contadorModal.valor, 10);
        if (isNaN(nuevo) || nuevo < 0) {
            return toast.error('El valor debe ser un entero mayor o igual a 0');
        }
        const p = contadorModal.prefijo;
        if (nuevo === p.ultimo_numero) {
            cerrarContador();
            return;
        }
        // Advertencia extra si el valor baja
        if (nuevo < p.ultimo_numero) {
            const ok = await confirm({
                title: '⚠️ Bajar el contador puede causar duplicados',
                message: (
                    <div>
                        <p>Estás bajando el contador de <strong>{p.ultimo_numero}</strong> a <strong>{nuevo}</strong>.</p>
                        <p className="text-sm text-muted" style={{ marginTop: 8 }}>
                            Si ya hay cotizaciones con correlativos mayores a {nuevo}, la próxima cotización que se cree con este prefijo intentará usar un número que <strong>ya existe</strong> y fallará.
                        </p>
                        <p className="text-sm text-muted">
                            El backend validará esto antes de guardar, pero es buena práctica verificar primero cuál es el correlativo más alto real.
                        </p>
                    </div>
                ),
                confirmText: 'Sí, bajar contador',
                danger: true
            });
            if (!ok) return;
        }
        try {
            await prefijosAPI.update(p.id, { ultimo_numero: nuevo });
            toast.success(`Contador actualizado a ${nuevo}`);
            cerrarContador();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al actualizar contador');
        }
    };

    if (loading) return <div className="loading">Cargando prefijos...</div>;

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Numeraciones</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <div>
                        <h3 className="h3">Prefijos de cotización</h3>
                        <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                            Cada prefijo tiene su propio contador. El default se usa cuando no eliges uno explícitamente.
                        </p>
                    </div>
                    <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={abrirCrear}>
                        Nuevo prefijo
                    </Button>
                </div>

                {prefijos.length === 0 ? (
                    <div className="empty-state" style={{ padding: 32, textAlign: 'center', color: 'rgb(var(--muted))' }}>
                        No hay prefijos configurados. Crea el primero para empezar.
                    </div>
                ) : (
                <div className="prefijos-list">
                    {prefijos.map(p => (
                        <Card
                            key={p.id}
                            className={`prefijo-card ${!p.activo ? 'is-inactive' : ''}`}
                        >
                            <Card.Body className="prefijo-card-body">
                                <div className="prefijo-main">
                                    <div className="prefijo-badge" style={{ background: `${p.color}22`, color: p.color, borderColor: `${p.color}55` }}>
                                        {p.prefijo}
                                    </div>
                                    <div className="prefijo-info">
                                        <div className="prefijo-nombre">
                                            {p.nombre}
                                            <Badge color={p.tipo === 'manual' ? 'warning' : 'info'} size="sm">
                                                {p.tipo === 'manual' ? 'Manual' : 'Normal'}
                                            </Badge>
                                            {p.es_default && (
                                                <Badge color="primary" size="sm">
                                                    <Star size={10} /> Default
                                                </Badge>
                                            )}
                                            {!p.activo && (
                                                <Badge color="neutral" size="sm">Inactivo</Badge>
                                            )}
                                        </div>
                                        {p.descripcion && (
                                            <div className="prefijo-desc">{p.descripcion}</div>
                                        )}
                                        <div className="prefijo-meta">
                                            Próximo: <strong>{p.prefijo}-{p.ultimo_numero + 1}</strong>
                                            {' · '}
                                            {p.cotizaciones_count} cotización{p.cotizaciones_count === 1 ? '' : 'es'} usan este prefijo
                                        </div>
                                    </div>
                                </div>
                                <div className="prefijo-actions">
                                    <Tooltip content={p.activo ? 'Editar contador' : 'Activa el prefijo para editar el contador'}>
                                        <Button variant="ghost" size="sm" onClick={() => abrirContador(p)} disabled={!p.activo}>
                                            # {p.ultimo_numero}
                                        </Button>
                                    </Tooltip>
                                    {!p.es_default && p.activo && (
                                        <Tooltip content="Marcar como default">
                                            <Button variant="ghost" size="sm" leftIcon={<Star size={13} />} onClick={() => handleSetDefault(p)}>
                                                Default
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip content={p.activo ? 'Desactivar' : 'Activar'}>
                                        <Button
                                            variant={p.activo ? 'ghost' : 'soft'}
                                            size="sm"
                                            leftIcon={p.activo ? <X size={13} /> : <Check size={13} />}
                                            onClick={() => handleToggleActivo(p)}
                                        >
                                            {p.activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content={p.activo ? 'Editar nombre/color/descripción' : 'Activa el prefijo para editarlo'}>
                                        <Button variant="ghost" size="sm" leftIcon={<Pencil size={13} />} onClick={() => abrirEditar(p)} disabled={!p.activo}>
                                            Editar
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content={p.cotizaciones_count > 0 ? `No se puede eliminar: ${p.cotizaciones_count} cotizaciones lo usan` : 'Eliminar prefijo'}>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<Trash2 size={13} />}
                                            onClick={() => handleEliminar(p)}
                                            disabled={p.cotizaciones_count > 0}
                                        />
                                    </Tooltip>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}

            {/* Modal crear/editar */}
            <Modal
                open={formModal.open}
                onClose={cerrarForm}
                title={formModal.editing ? 'Editar prefijo' : 'Nuevo prefijo'}
                size="md"
                footer={
                    <>
                        <Button variant="ghost" size="sm" onClick={cerrarForm}>Cancelar</Button>
                        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving}>
                            {saving ? 'Guardando...' : (formModal.editing ? 'Guardar cambios' : 'Crear prefijo')}
                        </Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <label className="label">
                            Prefijo <span className="text-xs text-muted">(letras y números, máx 20)</span>
                        </label>
                        <Input
                            value={formModal.data.prefijo}
                            onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, prefijo: e.target.value.toUpperCase() } }))}
                            placeholder="Ej: COT, MPW, ADV"
                            maxLength={20}
                            disabled={!!formModal.editing}
                        />
                        {formModal.editing && (
                            <p className="text-xs text-muted mt-1">
                                El código del prefijo no se puede cambiar (afectaría números ya generados).
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="label">Nombre</label>
                        <Input
                            value={formModal.data.nombre}
                            onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, nombre: e.target.value } }))}
                            placeholder="Ej: Cotización estándar, Marketing, Ventas..."
                            maxLength={100}
                        />
                    </div>
                    <div>
                        <label className="label">Tipo de cotización</label>
                        <div className="tipo-cot-buttons" role="group" aria-label="Tipo de cotización">
                            <button
                                type="button"
                                className={`tipo-cot-btn ${formModal.data.tipo === 'normal' ? 'is-active' : ''}`}
                                onClick={() => setFormModal(m => ({ ...m, data: { ...m.data, tipo: 'normal' } }))}
                                aria-pressed={formModal.data.tipo === 'normal'}
                            >
                                <strong>Normal</strong>
                                <span className="tipo-cot-hint">Cotizaciones habituales con servicios y personal</span>
                            </button>
                            <button
                                type="button"
                                className={`tipo-cot-btn ${formModal.data.tipo === 'manual' ? 'is-active' : ''}`}
                                onClick={() => setFormModal(m => ({ ...m, data: { ...m.data, tipo: 'manual' } }))}
                                aria-pressed={formModal.data.tipo === 'manual'}
                            >
                                <strong>Manual</strong>
                                <span className="tipo-cot-hint">Cotizaciones con documentos, monto ingresado a mano</span>
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="label">
                            Descripción <span className="text-xs text-muted">(opcional)</span>
                        </label>
                        <textarea
                            className="input"
                            style={{ minHeight: 60, resize: 'vertical', padding: 10 }}
                            value={formModal.data.descripcion}
                            onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, descripcion: e.target.value } }))}
                            placeholder="Para qué se usa este prefijo..."
                        />
                    </div>
                    <div>
                        <label className="label">Color (para diferenciar visualmente)</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {COLORES_SUGERIDOS.map(c => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setFormModal(m => ({ ...m, data: { ...m.data, color: c } }))}
                                    style={{
                                        width: 32, height: 32, borderRadius: 8,
                                        background: c,
                                        border: formModal.data.color === c ? '3px solid rgb(var(--text))' : '2px solid rgb(var(--line))',
                                        cursor: 'pointer'
                                    }}
                                    aria-label={c}
                                />
                            ))}
                            <input
                                type="color"
                                value={formModal.data.color}
                                onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, color: e.target.value } }))}
                                style={{ width: 40, height: 40, cursor: 'pointer', border: 'none', background: 'none' }}
                            />
                        </div>
                    </div>
                    {!formModal.editing && (
                        <>
                            <div>
                                <label className="label">
                                    Iniciar contador en <span className="text-xs text-muted">(por defecto 0 — la primera será {(formModal.data.prefijo || 'XXX').toUpperCase()}-1)</span>
                                </label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formModal.data.ultimo_numero}
                                    onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, ultimo_numero: e.target.value } }))}
                                />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={formModal.data.es_default}
                                    onChange={e => setFormModal(m => ({ ...m, data: { ...m.data, es_default: e.target.checked } }))}
                                />
                                <span className="text-sm">Marcar como default (desmarca el actual)</span>
                            </label>
                        </>
                    )}
                </div>
            </Modal>

            {/* Modal editar contador */}
            <Modal
                open={contadorModal.open}
                onClose={cerrarContador}
                title={`Editar contador de "${contadorModal.prefijo?.prefijo || ''}"`}
                size="sm"
                footer={
                    <>
                        <Button variant="ghost" size="sm" onClick={cerrarContador}>Cancelar</Button>
                        <Button variant="primary" size="sm" onClick={handleGuardarContador}>
                            Guardar contador
                        </Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{
                        padding: 10, borderRadius: 8,
                        background: 'rgb(var(--warning) / 0.08)',
                        border: '1px solid rgb(var(--warning) / 0.3)',
                        display: 'flex', gap: 8, alignItems: 'flex-start'
                    }}>
                        <AlertTriangle size={16} style={{ color: 'rgb(var(--warning))', flexShrink: 0, marginTop: 2 }} />
                        <div style={{ fontSize: 12 }}>
                            Cambiar el contador puede causar <strong>duplicados</strong> si bajás por debajo del correlativo más alto ya usado.
                            El sistema validará antes de guardar.
                        </div>
                    </div>
                    <div>
                        <label className="label">Último número usado</label>
                        <Input
                            type="number"
                            min="0"
                            value={contadorModal.valor}
                            onChange={e => setContadorModal(m => ({ ...m, valor: e.target.value }))}
                        />
                        <p className="text-xs text-muted mt-1">
                            Próxima cotización: <strong>{contadorModal.prefijo?.prefijo}-{(parseInt(contadorModal.valor, 10) || 0) + 1}</strong>
                        </p>
                    </div>
                </div>
            </Modal>
            </div>
        </div>
    </>
    );
};

export default NumeracionesPanel;
